<template>
  <JPageContainer title="회원사 관리" icon="users" description="회원사 정보 및 고객사 매핑을 관리합니다" :show-help="true" :content-scroll="false" @help="onHelp">

    <!-- ==================== 탭 ==================== -->
    <JTabs v-model:active-tab-id="activeTabId" :tabs="tabs" @tab-change="handleTabChange">
      <!-- ==================== Tab 1: 마스터 (회원사 기본정보) ==================== -->
      <template #content-master>
        <div class="flex flex-col h-full gap-2 p-2">
          <!-- 필터바 -->
          <JFilterBar
            v-model:collapsed="masterFilterCollapsed"
            v-model:filter-values="masterFilterValues"
            :filter-display="masterFilterDisplay"
            :collapsible="true"
            title="회원사 목록"
          >
            <template #actions>
              <JButton size="xs" variant="outline" @click="onMasterReset">초기화</JButton>
              <JButton size="xs" styletype="primary" @click="onMasterSearch">조회</JButton>
              <JButton size="xs" styletype="primary" @click="onMasterNew">신규</JButton>
            </template>

            <template #filters>
              <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                <JFormField
                  v-model="masterFilterValues.isActive"
                  type="combo"
                  label="활성여부"
                  :options="activeOptions"
                  orientation="horizontal"
                  label-width="30%"
                />
                <JFormField
v-model="masterFilterValues.keyword" type="input" label="검색어"
                  orientation="horizontal"
                  label-width="30%" 
                />
              </div>
            </template>
          </JFilterBar>

          <!-- 그리드 + 상세 영역 (Resizable) -->
          <JSplitter
            direction="horizontal"
            :default-size="60"
            :min-size="30"
            :second-min-size="20"
            :second-max-size="60"
            class="flex-1"
          >
            <!-- 좌측: 그리드 -->
            <template #left>
              <div class="h-full overflow-auto bg-background">
                <JGrid
                  ref="masterGridRef"
                  :column-defs="masterColumnDefs"
                  :row-data="masterRowData"
                  :action-buttons="masterActionButtons"
                  :enable-grouping="true"
                  :enable-pivot="true"
                  :enable-columns-tool-panel="true"
                  row-group-panel-show="always"
                  pivot-panel-show="always"
                  :group-default-expanded="1"
                  :compact-footer="true"
                  @row-clicked="onMasterRowClicked"
                />
              </div>
            </template>

            <!-- 우측: 상세 영역 -->
            <template #right>
              <div class="h-full overflow-y-auto bg-muted/30">
                <!-- 선택된 항목이 없을 때: EmptyState -->
                <JEmptyState
                  v-if="!masterDetail.memberId && !isMasterNew"
                  variant="simple"
                  icon="mousePointerClick"
                  title="항목을 선택하거나 신규 버튼을 클릭하세요"
                  class="h-full"
                />
                
                <!-- 선택된 항목이 있거나 신규일 때: 상세 폼 -->
                <JCard 
                  v-else
                  class="h-full" 
                  :title="isMasterNew ? '신규 등록' : '상세 정보'"
                  variant="elevated"
                >
                  <!-- actions 슬롯 (header 우측 버튼) -->
                  <template #actions>
                    <JButton size="xs" styletype="primary" @click="onMasterSave">저장</JButton>
                    <JButton v-if="!isMasterNew" size="xs" variant="destructive" @click="onMasterDelete">삭제</JButton>
                    <JButton size="xs" variant="outline" @click="onMasterReset">취소</JButton>
                  </template>

                  <!-- 2열 그리드: 회원사코드, 회원사명 -->
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                    <JFormField
                      v-model="masterDetail.memberId"
                      type="input"
                      label="회원사코드"
                      :readonly="!isMasterNew"
                      placeholder="회원사 코드"
                    />
                    <JFormField
                      v-model="masterDetail.memberName"
                      type="input"
                      label="회원사명"
                      placeholder="회원사 이름"
                    />
                  </div>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                    <!-- 활성여부 -->
                    <JFormField
                      v-model="masterDetail.isActive"
                      type="checkbox"
                      label="활성여부"
                      inline-label="활성"
                    />
                  </div>
                </JCard>
              </div>
            </template>
          </JSplitter>
        </div>
      </template>

      <!-- ==================== Tab 2: 매핑 (고객사 매핑) ==================== -->
      <template #content-mapping-customer>
        <div class="flex flex-col h-full gap-2 p-2">
          <!-- 필터바 -->
          <JFilterBar
            v-model:collapsed="mappingFilterCollapsed"
            v-model:filter-values="mappingFilterValues"
            :filter-display="mappingFilterDisplay"
            :collapsible="true"
            title="회원사별 고객사 매핑"
          >
            <template #actions>
              <JButton size="xs" variant="outline" @click="onMappingReset">초기화</JButton>
              <JButton size="xs" styletype="primary" @click="onMappingSearch">조회</JButton>
            </template>

            <template #filters>
              <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                <JFormField v-model="mappingFilterValues.keyword" type="input" label="검색어" />
              </div>
            </template>
          </JFilterBar>

          <!-- Vertical Splitter: 상단(마스터 그리드) / 하단(매핑 영역) -->
          <JSplitter
            direction="vertical"
            :default-size="30"
            :min-size="20"
            :second-min-size="40"
            class="flex-1"
          >
            <!-- 상단: 마스터 그리드 -->
            <template #top>
              <div class="h-full overflow-auto bg-background">
                <JGrid
                  ref="mappingMasterGridRef"
                  :column-defs="mappingMasterColumnDefs"
                  :row-data="masterRowData"
                  @row-clicked="onMappingMasterRowClicked"
                />
              </div>
            </template>

            <!-- 하단: 매핑 영역 (JShuttle 사용) -->
            <template #bottom>
              <div class="h-full">
                <!-- 회원사 미선택 시: EmptyState -->
                <JEmptyState
                  v-if="!selectedMemberId"
                  variant="simple"
                  icon="arrowLeftRight"
                  title="상단에서 회원사를 선택하세요"
                  class="h-full"
                />
                
                <!-- 회원사 선택 후: Shuttle -->
                <JShuttle
                  v-else
                  v-model:left-data="shuttleUnmappedData"
                  v-model:right-data="shuttleMappedData"
                  left-title="미매핑 고객사"
                  right-title="매핑된 고객사"
                  :column-defs="customerColumnDefs"
                  :searchable="false"
                  @move="onShuttleMove"
                />
              </div>
            </template>
          </JSplitter>
        </div>
      </template>
    </JTabs>
  </JPageContainer>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { JCard, JEmptyState } from '@/components/molecules'
import { JFilterBar, JShuttle } from '@/components/organisms'
import { JGrid, JButton, JSplitter, type ActionButton } from '@/components/atoms'
import { JFormField } from '@/components/molecules'
import { JTabs } from '@/components/molecules'

// ==================== 탭 상태 ====================
const activeTabId = ref('master')

const tabs = [
  { id: 'master', label: '회원사 기본정보' },
  { id: 'mapping-customer', label: '고객사 매핑' },
]

function handleTabChange(tabId: string) {
  console.log('탭 변경:', tabId)
}

// ==================== 공통 옵션 ====================
const activeOptions = [
  { value: 'Y', label: 'Y:활성' },
  { value: 'N', label: 'N:비활성' },
]

// ==================== Tab 1: 마스터 (회원사) ====================

const masterFilterCollapsed = ref(false)
const masterFilterValues = ref({
  isActive: '',
  keyword: '',
})

const masterFilterDisplay = {
  isActive: {
    label: '활성여부',
    displayValue: (val: unknown) => {
      if (val === 'Y') return 'Y:활성'
      if (val === 'N') return 'N:비활성'
      return ''
    },
  },
  keyword: {
    label: '검색어',
  },
}

// Mock 데이터 - 회원사 목록
const masterRowData = ref([
  { memberId: 'M001', memberName: '제이솔루션', isActive: 'Y' },
  { memberId: 'M002', memberName: 'ABC물류', isActive: 'Y' },
  { memberId: 'M003', memberName: 'XYZ유통', isActive: 'N' },
])

const masterColumnDefs = ref([
  { 
    field: 'memberId', 
    headerName: '회원사코드', 
    width: 150,
    enableValue: true,  // 집계 가능 (count)
  },
  { 
    field: 'memberName', 
    headerName: '회원사명', 
    flex: 1,
    enableValue: true,  // 집계 가능 (count)
  },
  {
    field: 'isActive',
    headerName: '활성',
    width: 100,
    cellRenderer: (params: any) => (params.value === 'Y' ? '✓' : ''),
    enableRowGroup: true,  // Row Group으로 사용 가능
    enablePivot: true,     // Pivot Column으로 사용 가능
  },
])

// 마스터 탭 행별 액션 버튼
const masterActionButtons: ActionButton[] = [
  {
    icon: 'pencil',
    label: '수정',
    tooltip: '편집',
    onClick: (rowData: any) => {
      isMasterNew.value = false
      masterDetail.value = {
        memberId: rowData.memberId,
        memberName: rowData.memberName,
        isActive: rowData.isActive,
      }
    },
  },
  {
    icon: 'trash2',
    label: '삭제',
    tooltip: '삭제',
    styletype: 'danger',
    onClick: (rowData: any) => {
      if (confirm(`${rowData.memberName}을(를) 삭제하시겠습니까?`)) {
        console.log('회원사 삭제:', rowData.memberId)
        alert(`삭제되었습니다: ${rowData.memberName}`)
      }
    },
  },
]

const masterGridRef = ref()
const isMasterNew = ref(false)
const masterDetail = ref({
  memberId: '',
  memberName: '',
  isActive: 'Y',
})

function onMasterRowClicked(event: any) {
  isMasterNew.value = false
  masterDetail.value = {
    memberId: event.data.memberId,
    memberName: event.data.memberName,
    isActive: event.data.isActive,
  }
}

function onMasterNew() {
  isMasterNew.value = true
  masterDetail.value = {
    memberId: '',
    memberName: '',
    isActive: 'Y',
  }
}

function onMasterSave() {
  console.log('회원사 저장:', masterDetail.value)
  // TODO: 실제 구현 시 API 호출로 대체
  alert(`저장되었습니다: ${masterDetail.value.memberName}`)
}

function onMasterDelete() {
  if (confirm(`${masterDetail.value.memberName}을(를) 삭제하시겠습니까?`)) {
    console.log('회원사 삭제:', masterDetail.value.memberId)
    // TODO: 실제 구현 시 API 호출로 대체
    alert(`삭제되었습니다: ${masterDetail.value.memberName}`)
  }
}

function onMasterReset() {
  masterFilterValues.value = {
    isActive: '',
    keyword: '',
  }
}

function onMasterSearch() {
  console.log('회원사 조회:', masterFilterValues.value)
  // TODO: 실제 구현 시 API 호출로 대체
}

function onHelp() {
  alert('회원사 관리 페이지 도움말')
}

// ==================== Tab 2: 매핑 (고객사) ====================

const mappingFilterCollapsed = ref(false)
const mappingFilterValues = ref({
  keyword: '',
})

const mappingFilterDisplay = {
  keyword: {
    label: '검색어',
  },
}

const mappingMasterGridRef = ref()

const mappingMasterColumnDefs = ref([
  { field: 'memberId', headerName: '회원사코드', width: 150 },
  { field: 'memberName', headerName: '회원사명', flex: 1 },
])

// Mock 데이터 - 전체 고객사 (매핑 여부 포함)
const selectedMemberId = ref<string>('')

// Mock 데이터 - 회원사별 매핑 정보
const mappingData = ref<Record<string, string[]>>({
  M001: ['C001', 'C002'], // 제이솔루션에 매핑된 고객사
  M002: ['C003'], // ABC물류에 매핑된 고객사
  M003: [], // XYZ유통에 매핑된 고객사 없음
})

const allCustomersData = [
  { id: 'C001', code: 'C001', name: '고객A' },
  { id: 'C002', code: 'C002', name: '고객B' },
  { id: 'C003', code: 'C003', name: '고객C' },
  { id: 'C004', code: 'C004', name: '고객D' },
  { id: 'C005', code: 'C005', name: '고객E' },
]

const customerColumnDefs = ref([
  { field: 'code', headerName: '코드', width: 120 },
  { field: 'name', headerName: '이름', flex: 1 },
])

// JShuttle용 데이터
const shuttleUnmappedData = ref<any[]>([])
const shuttleMappedData = ref<any[]>([])

function onMappingMasterRowClicked(event: any) {
  selectedMemberId.value = event.data.memberId
  console.log('선택된 회원사:', event.data.memberName)
  
  // JShuttle 데이터 업데이트
  const mappedCodes = mappingData.value[event.data.memberId] || []
  shuttleUnmappedData.value = allCustomersData.filter((c) => !mappedCodes.includes(c.code))
  shuttleMappedData.value = allCustomersData.filter((c) => mappedCodes.includes(c.code))
  
  // TODO: 실제 구현 시 API 호출로 매핑/미매핑 데이터 로드
}

/**
 * JShuttle 이동 이벤트 핸들러
 */
function onShuttleMove(event: any) {
  if (!selectedMemberId.value) {
    alert('회원사를 먼저 선택해주세요.')
    return
  }

  const { items, direction } = event
  const key = selectedMemberId.value

  if (direction === 'toRight') {
    // 매핑
    const codes = items.map((item: any) => item.code)
    if (!mappingData.value[key]) {
      mappingData.value[key] = []
    }
    mappingData.value[key]!.push(...codes)
    console.log('매핑:', codes)
    // TODO: 실제 구현 시 API 호출로 매핑 처리
  } else {
    // 해제
    const codes = items.map((item: any) => item.code)
    mappingData.value[key] = (mappingData.value[key] || []).filter(
      (code) => !codes.includes(code)
    )
    console.log('해제:', codes)
    // TODO: 실제 구현 시 API 호출로 매핑 해제 처리
  }
}

function onMappingReset() {
  mappingFilterValues.value = {
    keyword: '',
  }
}

function onMappingSearch() {
  console.log('매핑 조회:', mappingFilterValues.value)
  // TODO: 실제 구현 시 API 호출로 대체
}

defineExpose({ masterGridRef, mappingMasterGridRef })

// 초기 데이터 설정 (빈 상태)
shuttleUnmappedData.value = []
shuttleMappedData.value = []
</script>
